import React, {useEffect} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {Stack} from '@mui/material';
import {
  Body,
  designSystemToken,
  Headline,
  IconName,
} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import {CONTENT_STUDIO_VIEW_NAMES} from '@/lib/analytics/analyticsConstants';
import queryClient from '@/utils/reactQueryClient';
import translate from '@/utils/translate';
import {getEnv} from '@/config/environment';
import {ROUTES} from '@/config/routesConstants';
import {
  FEATURE_ASSETS,
  FEATURE_ID,
  FeatureId,
} from '@/views/content-studio/constants';
import {CONTENT_LAB, FOLDERS} from '@/hooks/content-studio/consts';
import useNavigation from '@/hooks/use-navigation';
import useZendeskScript from '@/hooks/use-zendesk-script';
import contentStudioStore from '@/stores/contentStudioStore';
import FeatureCard from '../feature-card';
import FileUploader from '../file-upload';
import Projects from '../projects';

type Feature = {
  id: FeatureId;
  title: string;
  description: string;
  iconName: IconName;
  videoPath: string;
  thumbnailPath: string;
  path: string;
};

const CONTENT_STUDIO_FEATURES: Feature[] = [
  {
    id: FEATURE_ID.RESIZE,
    title: translate('Resize to Any Ratio'),
    description: translate(
      'Automatically create multiple versions of your video content'
    ),
    iconName: 'Features-Crop',
    videoPath: FEATURE_ASSETS.RESIZE.VIDEO,
    thumbnailPath: FEATURE_ASSETS.RESIZE.IMAGE,
    path: `${ROUTES.CONTENT_LAB_REFORMAT_ASSETS}`,
  },
  {
    id: FEATURE_ID.DUB,
    title: translate('Dub to Multiple Languages'),
    description: translate('Translate and dub content to reach new markets'),
    iconName: 'Navigation-Messages',
    videoPath: FEATURE_ASSETS.DUB.VIDEO,
    thumbnailPath: FEATURE_ASSETS.DUB.IMAGE,
    path: `${ROUTES.CONTENT_LAB_TRANSLATE_ASSETS}`,
  },
  {
    id: FEATURE_ID.REPHRASE,
    title: translate('Rephrase Messaging'),
    description: translate('Tailor your script without starting from scratch'),
    iconName: 'Features-Rename',
    videoPath: FEATURE_ASSETS.REPHRASE.VIDEO,
    thumbnailPath: FEATURE_ASSETS.REPHRASE.IMAGE,
    path: `${ROUTES.CONTENT_LAB_REPHRASE_ASSETS}`,
  },
];

function FeaturesSection({features}: {features: Feature[]}) {
  const {navigate} = useNavigation();
  const {uiInteraction} = useUiInteractionEvent();
  return (
    <Stack alignItems="center" width="100%" position="relative">
      <Stack
        direction="row"
        gap="16px"
        py="16px"
        maxWidth="100%"
        sx={{
          'overflowX': 'auto',
          '&::-webkit-scrollbar': {
            display: 'none' /* Chrome, Safari, Opera */,
          },
          '& > *': {
            flex: '0 0 auto' /* Prevent items from shrinking */,
          },
        }}
      >
        {features.map((feature) => (
          <FeatureCard
            key={feature.title}
            title={translate(feature.title)}
            description={translate(feature.description)}
            iconName={feature.iconName}
            videoPath={feature.videoPath}
            thumbnailPath={feature.thumbnailPath}
            onClick={() => {
              uiInteraction({
                item_name: `${feature.title}_card`,
                interaction_location: CONTENT_STUDIO_VIEW_NAMES.HOME,
              });
              navigate(feature.path);
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
}

function Home() {
  useZendeskScript(getEnv().VITE_CONTENT_STUDIO_ZENDESK_KEY, true);
  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: [CONTENT_LAB, FOLDERS],
    });
  }, []);
  const {selectedAssets} = contentStudioStore(
    useShallow((state) => ({
      selectedAssets: state.selectedAssets,
    }))
  );

  return (
    <Stack
      direction="column"
      width="100%"
      alignItems="center"
      py={5}
      sx={{
        height: '100vh',
        overflowY: 'auto',
      }}
    >
      {/* Header */}
      <Stack
        direction="column"
        width="100%"
        alignItems="center"
        marginBottom={3}
      >
        <Headline size="xl" marginBottom={1}>
          {translate('Welcome to Ltx Scaler')}
        </Headline>
        <Body size="lg" color={designSystemToken('semantic.fg.secondary')}>
          {translate(
            'From one video to dozens, quickly scale your content with powerful AI tools.'
          )}
        </Body>
      </Stack>

      {/* Features Section */}
      <FeaturesSection features={CONTENT_STUDIO_FEATURES} />

      {/* Assets Uploader */}
      <Headline
        size="lg"
        style={{
          textAlign: 'start',
          maxWidth: '1088px',
          width: '100%',
          marginTop: '40px',
          marginBottom: '16px',
        }}
      >
        {translate('Start Uploading Files')}
      </Headline>
      <FileUploader
        width="100%"
        height={selectedAssets.length === 0 ? '296px' : undefined}
        maxWidth="1088px"
        analyticsLocation={CONTENT_STUDIO_VIEW_NAMES.HOME}
      />

      {/* Projects Section */}
      <Projects />
    </Stack>
  );
}

export default Home;
